// Safeguards register and evidence line client (plan §4, spec F7 and J5; T26). Writes carry the
// line's state version as If-Match and an Idempotency-Key, so a retry on a slow phone adds one
// record (spec §8.8). The portal never fetches the evidence link itself (spec §5.5).
import type { Http } from './http.ts';
import { pathSegment } from './paths.ts';
import {
  evidenceLineSchema,
  frozenEvidenceSchema,
  frozenTaskSchema,
  safeguardPageSchema,
  type EvidenceLine,
  type FrozenEvidence,
  type FrozenTask,
  type SafeguardPage,
  type SafeguardStatus,
  type SafeguardSummary,
} from './schemas/evidence.ts';

export * from './schemas/evidence.ts';

export interface SafeguardFilters {
  /** CIS control number, "4". */
  readonly family?: string | undefined;
  readonly status?: SafeguardStatus | undefined;
  readonly q?: string | undefined;
}

export interface NewEvidenceRecord {
  readonly url: string;
  /** 64 lowercase hex digits, or left out. */
  readonly sha256?: string | undefined;
  readonly capturedOn: string;
  readonly note?: string | undefined;
}

export type EvidenceLineAction = 'check' | 'return' | 'set-gap';

export interface EvidenceActOptions {
  readonly action: EvidenceLineAction;
  readonly version: number;
  /** Required by return: why the line goes back to planned. Set-gap keeps it as the why. */
  readonly reason?: string | undefined;
  /** Required by set-gap (owner only): the exception id, "EXC-A1" (spec J5 step 2). */
  readonly exceptionId?: string | undefined;
  readonly idempotencyKey: string;
}

export interface EvidenceApi {
  /** Every safeguard that matches, all pages (153 in the real pack, so two requests at most). */
  safeguards(filters: SafeguardFilters, signal?: AbortSignal): Promise<SafeguardSummary[]>;
  /** GET /evidence/:code, or the line rebuilt from history at the end of `asOf`. */
  line(
    code: string,
    opts?: { readonly asOf?: string | undefined; readonly signal?: AbortSignal | undefined },
  ): Promise<EvidenceLine>;
  /** POST /evidence/:code/records: answers the updated line. */
  addRecord(
    code: string,
    record: NewEvidenceRecord,
    opts: { readonly version: number; readonly idempotencyKey: string },
  ): Promise<EvidenceLine>;
  /** POST /evidence/:code/actions/:action: answers the updated line. */
  act(code: string, opts: EvidenceActOptions): Promise<EvidenceLine>;
  /** GET /freezes/:id/evidence/:code: the line as the freeze stored it (T32). */
  frozenLine(freezeId: number, code: string, signal?: AbortSignal): Promise<FrozenEvidence>;
  /** GET /freezes/:id/tasks/:taskId: the task as the freeze stored it (T32). */
  frozenTask(freezeId: number, taskId: string, signal?: AbortSignal): Promise<FrozenTask>;
}

const PAGE_SIZE = 100; // the API's pageSize limit (constitution §4.13)
// A stop for a server that keeps answering more pages than it said it had.
const MAX_PAGES = 20;
const SECOND_PAGE = 2;

/** `?page=..&pageSize=100&family=..` in one fixed order, so equal filters give equal URLs. */
export function safeguardQuery(filters: SafeguardFilters, page: number): string {
  const search = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  if (filters.family !== undefined) search.set('family', filters.family);
  if (filters.status !== undefined) search.set('status', filters.status);
  const q = filters.q?.trim() ?? '';
  if (q !== '') search.set('q', q);
  return `?${search.toString()}`;
}

function recordBody(record: NewEvidenceRecord): Record<string, string> {
  const body: Record<string, string> = { url: record.url.trim(), capturedOn: record.capturedOn };
  const sha = record.sha256?.trim() ?? '';
  const note = record.note?.trim() ?? '';
  if (sha !== '') body['sha256'] = sha;
  if (note !== '') body['note'] = note;
  return body;
}

async function allSafeguards(
  http: Http,
  filters: SafeguardFilters,
  signal: AbortSignal | undefined,
): Promise<SafeguardSummary[]> {
  const pageOf = (page: number): Promise<SafeguardPage> =>
    http.json(
      { method: 'GET', path: `/safeguards${safeguardQuery(filters, page)}`, signal },
      safeguardPageSchema,
    );
  const first = await pageOf(1);
  const pages = Math.min(MAX_PAGES, Math.ceil(first.total / PAGE_SIZE));
  const rest = await Promise.all(
    // Pages 2..n: index 0 is page 2.
    Array.from({ length: Math.max(0, pages - 1) }, (_, i) => pageOf(i + SECOND_PAGE)),
  );
  return [first, ...rest].flatMap((p) => p.items);
}

// Frozen views are parsed with this file's schemas, which allow for a freeze label that the
// server's Leads redaction made longer than the label cap (review F-R1), so a helper's frozen
// view does not fail on the label.
const frozenPath = (freezeId: number, kind: 'evidence' | 'tasks', key: string): string =>
  `/freezes/${pathSegment(String(freezeId))}/${kind}/${pathSegment(key)}`;

export function createEvidenceApi(http: Http): EvidenceApi {
  const linePath = (code: string): string => `/evidence/${pathSegment(code)}`;
  // Methods are async so a refused code is a rejected promise, never a synchronous throw.
  return {
    safeguards: async (filters, signal) => allSafeguards(http, filters, signal),
    line: async (code, opts = {}) => {
      const query = opts.asOf === undefined ? '' : `?${new URLSearchParams({ asOf: opts.asOf })}`;
      return http.json(
        { method: 'GET', path: `${linePath(code)}${query}`, signal: opts.signal },
        evidenceLineSchema,
      );
    },
    addRecord: async (code, record, opts) =>
      http.json(
        {
          method: 'POST',
          path: `${linePath(code)}/records`,
          version: opts.version,
          idempotencyKey: opts.idempotencyKey,
          body: recordBody(record),
        },
        evidenceLineSchema,
      ),
    act: async (code, opts) => {
      const reason = opts.reason?.trim() ?? '';
      const exceptionId = opts.exceptionId?.trim() ?? '';
      return http.json(
        {
          method: 'POST',
          path: `${linePath(code)}/actions/${pathSegment(opts.action)}`,
          version: opts.version,
          idempotencyKey: opts.idempotencyKey,
          body: {
            ...(reason === '' ? {} : { reason }),
            ...(exceptionId === '' ? {} : { exceptionId }),
          },
        },
        evidenceLineSchema,
      );
    },
    frozenLine: async (freezeId, code, signal) =>
      http.json(
        { method: 'GET', path: frozenPath(freezeId, 'evidence', code), signal },
        frozenEvidenceSchema,
      ),
    frozenTask: async (freezeId, taskId, signal) =>
      http.json(
        { method: 'GET', path: frozenPath(freezeId, 'tasks', taskId), signal },
        frozenTaskSchema,
      ),
  };
}

/** The evidence page body of a frozen view, or null when it has another shape. */
export function parseFrozenEvidence(view: unknown): FrozenEvidence | null {
  const parsed = frozenEvidenceSchema.safeParse(view);
  return parsed.success ? parsed.data : null;
}

/** The task page body of a frozen view, or null when it has another shape. */
export function parseFrozenTask(view: unknown): FrozenTask | null {
  const parsed = frozenTaskSchema.safeParse(view);
  return parsed.success ? parsed.data : null;
}
